import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import SectionHeader from "../SectionHeader/SectionHeader";
import { personalInfo } from "../../data/portfolio";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import { FiMail, FiMapPin, FiPhone, FiSend } from "react-icons/fi";
import "./Contact.css";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

function Contact() {
  const { email, phone, location } = personalInfo;
  const formElement = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      await emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, formElement.current, PUBLIC_KEY);
      setStatus({ type: "success", message: "Message sent successfully!" });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const [infoRef, infoVisible] = useScrollReveal(0.2);
  const [formRef, formVisible] = useScrollReveal(0.2);

  return (
    <section className="contact section" id="contact">
      <div className="container">
        <SectionHeader
          title="Get In Touch"
          subtitle="Let's build something together"
        />

        <div className="contact__grid">
          <div ref={infoRef} className={`contact__info reveal reveal--left ${infoVisible ? 'reveal--visible' : ''}`}>
            <h3 className="contact__info-title">Let’s connect</h3>
            <p className="contact__info-text">
              Whether you're building a product, scaling a platform, or solving
              a complex engineering problem, I’m always open to meaningful
              conversations.
            </p>

            <div className="contact__details">
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FiMail />
                </div>
                <div>
                  <p className="contact__detail-label">Email</p>
                  <a href={`mailto:${email}`} className="contact__detail-value">
                    {email}
                  </a>
                </div>
              </div>
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FiPhone />
                </div>
                <div>
                  <p className="contact__detail-label">Phone</p>
                  <p className="contact__detail-value">{phone}</p>
                </div>
              </div>
              <div className="contact__detail">
                <div className="contact__detail-icon">
                  <FiMapPin />
                </div>
                <div>
                  <p className="contact__detail-label">Location</p>
                  <p className="contact__detail-value">{location}</p>
                </div>
              </div>
            </div>
          </div>

          <form ref={(el) => { formElement.current = el; formRef.current = el; }} className={`contact__form reveal reveal--right ${formVisible ? 'reveal--visible' : ''}`} onSubmit={handleSubmit}>
            <div className="contact__form-row">
              <div className="contact__form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                />
              </div>
              <div className="contact__form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="contact__form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Project Inquiry"
              />
            </div>

            <div className="contact__form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell me about your project..."
              />
            </div>

            {status.message && (
              <div
                className={`contact__status contact__status--${status.type}`}
              >
                {status.message}
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary contact__submit"
              disabled={loading}
            >
              {loading ? (
                "Sending..."
              ) : (
                <>
                  <FiSend /> Send Message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;
